"use strict";
/**
 * Main Application Entry Point
 * Initializes the app and handles routing
 */
Object.defineProperty(exports, "__esModule", { value: true });
const state_1 = require("./state");
const auth_1 = require("./auth");
const api_1 = require("./api");
const login_handler_1 = require("./handlers/login-handler");
const events_handler_1 = require("./handlers/events-handler");
const order_handler_1 = require("./handlers/order-handler");
const pair_builder_handler_1 = require("./handlers/pair-builder-handler");
const monitor_status_handler_1 = require("./handlers/monitor-status-handler");
const pnl_handler_1 = require("./handlers/pnl-handler");
const modal_handlers_1 = require("./handlers/modal-handlers");
const theme_handler_1 = require("./handlers/theme-handler");
const state_handler_1 = require("./services/state-handler");
const ui_1 = require("./ui");
const side_label_1 = require("./ui/side-label");
const venue_status_indicator_1 = require("./ui/venue-status-indicator");
const price_sample_button_1 = require("./ui/price-sample-button");
const venue_status_service_1 = require("./services/venue-status-service");
const event_type_service_1 = require("./services/event-type-service");
const win_prob_handler_1 = require("./handlers/win-prob-handler");
const rotation_scroll_reset_1 = require("./services/rotation-scroll-reset");
const page_navigation_1 = require("./ui/page-navigation");
const state_2 = require("./state");
const permissions_1 = require("./permissions");
const route_applier_1 = require("./services/route-applier");
const navigation_service_1 = require("./services/navigation-service");
/**
 * Initialize the application
 */
function initApp() {
    // Load settings from sessionStorage
    (0, state_1.loadSettingsFromStorage)();
    // Load data source from localStorage
    (0, state_1.loadDataSourceFromStorage)();
    // Cleanup stale event type entries (nightly cleanup)
    (0, event_type_service_1.cleanupStaleEventTypes)();
    // SECURITY: credentials are no longer persisted in sessionStorage —
    // remove any copy left behind by older versions of the app.
    (0, api_1.purgeLegacyStoredCredentials)();
    // Try to restore previous session
    (0, auth_1.restoreSession)();
    // iOS home-screen apps can hit-test taps off the finger after a rotation (N3 PLAN A211).
    (0, rotation_scroll_reset_1.installRotationScrollReset)();
    // Setup all event listeners
    (0, login_handler_1.setupLoginForm)();
    (0, events_handler_1.setupEventsPage)();
    (0, order_handler_1.setupOrderPage)();
    (0, pair_builder_handler_1.setupPairBuilderPage)();
    (0, monitor_status_handler_1.setupMonitorStatusPage)();
    (0, pnl_handler_1.setupPnlPage)();
    (0, win_prob_handler_1.setupWinProbPage)({ onExit: navigation_service_1.navigateToEvents });
    // /app/* URLs: held through sign-in, owner-gated for win-prob (services/route-applier.ts).
    const routes = (0, route_applier_1.createRouteApplier)({
        getState: state_2.getState,
        canWinProb: permissions_1.canWinProb,
        openWinProb: (segments, query) => (0, navigation_service_1.navigateToWinProb)(segments, query),
        getSearch: () => window.location.search,
        // Back out of a routed page. `page` is whatever was stamped on the
        // entry being restored, or null when nothing was; either way the app
        // has to move, because a Back press that only rewrites the address bar
        // looks broken.
        navigate: (page) => (0, navigation_service_1.navigateToPage)(isRestorablePage(page) ? page : "events"),
    });
    (0, modal_handlers_1.setupSettingsModal)();
    (0, modal_handlers_1.setupCredentialsModal)();
    (0, modal_handlers_1.setupReAuthModal)();
    (0, modal_handlers_1.setupMarketSelectionModal)();
    (0, theme_handler_1.setupThemeToggle)();
    // Subscribe to state changes: the one subscription (app-startup test).
    (0, state_1.subscribe)((state) => {
        (0, state_handler_1.handleStateChange)(state);
        routes.onState(state);
    });
    // Attach tap-to-toggle for price badges
    (0, ui_1.initExtraInfoToggle)();
    // Tap-to-reveal on the sport-mode side labels, and re-fit them to their
    // (half-width) columns whenever the viewport changes.
    (0, side_label_1.initSideLabels)();
    // Corner venue-health dot + its slow status poller (both no-ops until
    // authenticated; never touch the order path).
    (0, venue_status_indicator_1.initVenueStatusIndicator)();
    (0, venue_status_service_1.startVenueStatusPolling)();
    // The MARK button between the orderbook columns: hidden until an order
    // fills, then one press records the displayed book against that order.
    (0, price_sample_button_1.initPriceSampleButton)();
    // A /app/* URL restores that page on load and on Back/Forward. Opened while
    // signed out, it is held and applied after sign-in (services/route-applier.ts).
    (0, page_navigation_1.initAppRouting)(routes.apply);
}
/**
 * Pages the router may restore on a Back press. "login" is excluded on
 * purpose: leaving the app signed-in state to a history entry would be a way
 * to appear signed out, or worse, signed in.
 */
const RESTORABLE_PAGES = [
    "events",
    "order",
    "pair-builder",
    "monitor-status",
    "pnl",
    "win-prob",
];
function isRestorablePage(page) {
    return page !== null && RESTORABLE_PAGES.includes(page);
}
/**
 * Start the application when DOM is ready
 */
if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initApp);
}
else {
    initApp();
}
